// frontend/src/pages/Relatorios.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const C = {
  navy:    '#1A2233', orange:  '#D85A30', orangeH: '#B84A24',
  bg:      '#f5f7fa', surface: '#ffffff', border:  '#d4dde8',
  muted:   '#6b7a8d', green:   '#27ae60', red:     '#e74c3c',
  blue:    '#2563eb', yellow:  '#f59e0b',
};
const CHART_COLORS = ['#D85A30','#1A2233','#27ae60','#2563eb','#f59e0b','#8b5cf6','#06b6d4','#ec4899'];
const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const brl = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
function mesAtual() { const d = new Date(); return { mes: d.getMonth() + 1, ano: d.getFullYear() }; }

const statusLabel = { aberto: 'Aberto', fechado: 'Concluído', em_atendimento: 'Em Atendimento' };
const statusCor   = { aberto: C.orange, fechado: C.green, em_atendimento: C.blue };
const statusBg    = { aberto: '#fff7ed', fechado: '#f0fdf4', em_atendimento: '#eff6ff' };

// ── Botão Imprimir ─────────────────────────────────────────────────────────
function BotaoImprimir({ conteudoRef, titulo }) {
  const handlePrint = () => {
    const html = conteudoRef?.current?.innerHTML || '';
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${titulo}</title>
<style>
*{box-sizing:border-box}
body{font-family:Arial,sans-serif;font-size:12px;color:#1A2233;margin:0;padding:20px;background:white}
h2{color:#1A2233;margin:0 0 14px;font-size:16px}
.kpi-row{display:flex;gap:10px;flex-wrap:wrap;margin-bottom:14px}
.kpi-card{border:1px solid #d4dde8;border-radius:8px;padding:12px 14px;min-width:110px;flex:1}
.kpi-label{font-size:9px;font-weight:700;color:#6b7a8d;text-transform:uppercase;letter-spacing:.6px;margin-bottom:4px}
.kpi-valor{font-size:17px;font-weight:900}
.kpi-sub{font-size:10px;color:#6b7a8d;margin-top:2px}
.card{border:1px solid #d4dde8;border-radius:8px;padding:14px;margin-bottom:12px}
.sec-title{font-size:10px;font-weight:700;color:#1A2233;text-transform:uppercase;letter-spacing:.8px;border-left:3px solid #D85A30;padding-left:8px;margin:0 0 10px}
table{width:100%;border-collapse:collapse;font-size:11px}
th{background:#f5f7fa;padding:7px 8px;font-size:9px;font-weight:700;text-transform:uppercase;color:#6b7a8d;text-align:left;white-space:nowrap}
td{padding:7px 8px;border-bottom:1px solid #f0f3f7;vertical-align:top}
.badge{display:inline-block;padding:2px 6px;border-radius:20px;font-size:9px;font-weight:700}
.no-print{display:none!important}
@media print{body{padding:10px}}
</style>
</head><body>${html}</body></html>`);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); win.close(); }, 400);
  };
  return (
    <button onClick={handlePrint} className="no-print" style={{
      display:'flex',alignItems:'center',gap:6,padding:'8px 16px',
      border:`1.5px solid ${C.border}`,borderRadius:8,background:'white',
      color:C.navy,fontSize:13,fontWeight:600,cursor:'pointer',transition:'all .15s',
    }}
      onMouseEnter={e=>{e.currentTarget.style.borderColor=C.orange;e.currentTarget.style.color=C.orange;}}
      onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.color=C.navy;}}
    >🖨️ Imprimir</button>
  );
}

// ── UI helpers ─────────────────────────────────────────────────────────────
function Card({children,style={}}){
  return <div className="card" style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:'18px 20px',boxShadow:'0 1px 4px rgba(0,0,0,.04)',...style}}>{children}</div>;
}
function KPIRow({children}){
  return <div className="kpi-row" style={{display:'flex',gap:12,flexWrap:'wrap'}}>{children}</div>;
}
function KPI({label,valor,sub,cor=C.navy,icone}){
  return(
    <div className="kpi-card" style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:'14px 16px',flex:1,minWidth:110,boxShadow:'0 1px 4px rgba(0,0,0,.04)'}}>
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:5}}>
        {icone&&<span style={{fontSize:16}}>{icone}</span>}
        <div className="kpi-label" style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:.6}}>{label}</div>
      </div>
      <div className="kpi-valor" style={{fontSize:20,fontWeight:900,color:cor,lineHeight:1}}>{valor}</div>
      {sub&&<div className="kpi-sub" style={{fontSize:11,color:C.muted,marginTop:3}}>{sub}</div>}
    </div>
  );
}
function SecTitle({children}){
  return <div className="sec-title" style={{fontSize:11,fontWeight:700,color:C.navy,textTransform:'uppercase',letterSpacing:.8,borderLeft:`3px solid ${C.orange}`,paddingLeft:10,marginBottom:12}}>{children}</div>;
}
function Spinner(){
  return(
    <div style={{textAlign:'center',padding:'48px 0',color:C.muted,fontSize:14}}>
      <div style={{width:32,height:32,border:`3px solid ${C.border}`,borderTop:`3px solid ${C.orange}`,borderRadius:'50%',animation:'spin .8s linear infinite',margin:'0 auto 12px'}}/>
      Carregando...
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
function Vazio({msg='Nenhum dado encontrado para este período.'}){
  return <div style={{textAlign:'center',padding:'48px 0',color:C.muted,fontSize:14}}>📭 {msg}</div>;
}
const CustomTooltip=({active,payload,label,prefixo})=>{
  if(!active||!payload?.length) return null;
  return(
    <div style={{background:C.navy,color:'white',borderRadius:8,padding:'8px 14px',fontSize:12,boxShadow:'0 4px 16px rgba(0,0,0,.2)'}}>
      {label&&<div style={{fontWeight:700,marginBottom:3}}>{label}</div>}
      {payload.map((p,i)=>(
        <div key={i} style={{color:p.color||'white'}}>{p.name}: {prefixo==='brl'?brl(p.value):p.value}</div>
      ))}
    </div>
  );
};

// Estilos de tabela
const th={padding:'9px 10px',fontWeight:700,fontSize:10,color:C.muted,textTransform:'uppercase',letterSpacing:.6,textAlign:'left',whiteSpace:'nowrap',background:C.bg};
const td={padding:'9px 10px',color:C.navy,fontSize:12,verticalAlign:'top'};

function StatusBadge({status}){
  return(
    <span className="badge" style={{display:'inline-block',padding:'2px 7px',borderRadius:20,fontSize:10,fontWeight:700,background:statusBg[status]||C.bg,color:statusCor[status]||C.muted}}>
      {statusLabel[status]||status}
    </span>
  );
}

function FiltroMes({mes,ano,onChange}){
  const anos=[]; const anoAtual=new Date().getFullYear();
  for(let a=anoAtual;a>=anoAtual-3;a--) anos.push(a);
  const inp={padding:'8px 12px',border:`1.5px solid ${C.border}`,borderRadius:8,fontSize:14,color:C.navy,background:'white',cursor:'pointer',outline:'none'};
  return(
    <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
      <select value={mes} onChange={e=>onChange(Number(e.target.value),ano)} style={inp}>
        {MESES.map((m,i)=><option key={i+1} value={i+1}>{m}</option>)}
      </select>
      <select value={ano} onChange={e=>onChange(mes,Number(e.target.value))} style={inp}>
        {anos.map(a=><option key={a} value={a}>{a}</option>)}
      </select>
    </div>
  );
}

// ── ABA Reembolsos ─────────────────────────────────────────────────────────
function RelReembolso({mes,ano}){
  const [dados,setDados]=useState(null);
  const [loading,setLoading]=useState(true);
  const ref=useRef();
  useEffect(()=>{
    setLoading(true);
    api.get(`/relatorios/reembolsos?mes=${mes}&ano=${ano}`)
      .then(r=>setDados(r.data)).catch(()=>setDados(null)).finally(()=>setLoading(false));
  },[mes,ano]);
  if(loading) return <Spinner/>;
  if(!dados) return <Vazio msg="Erro ao carregar dados."/>;
  const {resumo,porCategoria,porFuncionario}=dados;
  const temDados=Number(resumo?.quantidade||0)>0;
  return(
    <div>
      <div className="no-print" style={{display:'flex',justifyContent:'flex-end',marginBottom:14}}>
        <BotaoImprimir conteudoRef={ref} titulo={`Reembolsos — ${MESES[mes-1]} ${ano}`}/>
      </div>
      <div ref={ref}>
        <h2 style={{margin:'0 0 14px',fontSize:16,color:C.navy}}>💰 Reembolsos — {MESES[mes-1]} / {ano}</h2>
        <KPIRow>
          <KPI label="Total Reembolsado" valor={brl(resumo?.total)} icone="💰" cor={C.orange}/>
          <KPI label="Lançamentos Pagos" valor={resumo?.quantidade||0} icone="✅" cor={C.green}/>
          <KPI label="Pendentes" valor={resumo?.pendentes||0} icone="⏳" cor={C.yellow}/>
          <KPI label="Valor Pendente" valor={brl(resumo?.total_pendente)} icone="🕐" cor={C.muted}/>
        </KPIRow>
        {!temDados?<div style={{marginTop:20}}><Vazio/></div>:(
          <div style={{display:'flex',flexDirection:'column',gap:16,marginTop:16}}>
            {porCategoria.length>0&&(
              <Card>
                <SecTitle>Por Categoria</SecTitle>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={porCategoria} margin={{left:10,right:10}}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border}/>
                    <XAxis dataKey="categoria" tick={{fontSize:11,fill:C.muted}}/>
                    <YAxis tick={{fontSize:11,fill:C.muted}} tickFormatter={v=>`R$${(v/1000).toFixed(0)}k`}/>
                    <Tooltip content={<CustomTooltip prefixo="brl"/>}/>
                    <Bar dataKey="total" name="Total" radius={[4,4,0,0]}>
                      {porCategoria.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%CHART_COLORS.length]}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            )}
            {porFuncionario.filter(f=>f.total>0).length>0&&(
              <Card>
                <SecTitle>Por Funcionário</SecTitle>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead><tr><th style={th}>Funcionário</th><th style={{...th,textAlign:'right'}}>Qtd.</th><th style={{...th,textAlign:'right'}}>Total</th></tr></thead>
                  <tbody>
                    {porFuncionario.filter(f=>f.total>0).map((f,i)=>(
                      <tr key={i} style={{borderBottom:`1px solid ${C.border}`}}>
                        <td style={td}>
                          <div style={{display:'flex',alignItems:'center',gap:8}}>
                            <div style={{width:24,height:24,borderRadius:'50%',background:C.orange,color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700,flexShrink:0}}>{f.iniciais}</div>
                            {f.nome}
                          </div>
                        </td>
                        <td style={{...td,textAlign:'right',color:C.muted}}>{f.quantidade}</td>
                        <td style={{...td,textAlign:'right',fontWeight:700,color:C.orange}}>{brl(f.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── ABA Financeiro ─────────────────────────────────────────────────────────
function RelFinanceiro({mes,ano,tipo}){
  const [dados,setDados]=useState(null);
  const [loading,setLoading]=useState(true);
  const ref=useRef();
  useEffect(()=>{
    setLoading(true);
    api.get(`/relatorios/financeiro?mes=${mes}&ano=${ano}`)
      .then(r=>setDados(r.data)).catch(()=>setDados(null)).finally(()=>setLoading(false));
  },[mes,ano]);
  if(loading) return <Spinner/>;
  if(!dados) return <Vazio msg="Erro ao carregar dados."/>;
  const lista=tipo==='pagar'?dados.pagar:dados.receber;
  const categorias=tipo==='pagar'?dados.pagarCategoria:dados.receberCategoria;
  const isPagar=tipo==='pagar';
  const pago=lista.find(p=>p.status==='pago')||{total:0,quantidade:0};
  const pendente=lista.find(p=>p.status==='pendente')||{total:0,quantidade:0};
  const vencido=lista.find(p=>p.status==='vencido')||{total:0,quantidade:0};
  const totalGeral=lista.reduce((a,b)=>a+Number(b.total),0);
  const temDados=totalGeral>0;
  const pieData=lista.map(p=>({
    name:{pago:isPagar?'Pago':'Recebido',pendente:'Pendente',vencido:'Vencido',cancelado:'Cancelado'}[p.status]||p.status,
    value:Number(p.total),
  })).filter(p=>p.value>0);
  const titulo=isPagar?'📤 Contas a Pagar':'📥 Contas a Receber';
  const corValor=isPagar?C.red:C.green;
  return(
    <div>
      <div className="no-print" style={{display:'flex',justifyContent:'flex-end',marginBottom:14}}>
        <BotaoImprimir conteudoRef={ref} titulo={`${isPagar?'Contas a Pagar':'Contas a Receber'} — ${MESES[mes-1]} ${ano}`}/>
      </div>
      <div ref={ref}>
        <h2 style={{margin:'0 0 14px',fontSize:16,color:C.navy}}>{titulo} — {MESES[mes-1]} / {ano}</h2>
        <KPIRow>
          <KPI label="Total Geral" valor={brl(totalGeral)} icone="📋" cor={C.navy}/>
          <KPI label={isPagar?'Pago':'Recebido'} valor={brl(pago.total)} sub={`${pago.quantidade} lançamento(s)`} icone="✅" cor={C.green}/>
          <KPI label="Pendente" valor={brl(pendente.total)} sub={`${pendente.quantidade} lançamento(s)`} icone="⏳" cor={isPagar?C.yellow:C.blue}/>
          <KPI label="Vencido" valor={brl(vencido.total)} sub={`${vencido.quantidade} lançamento(s)`} icone="🚨" cor={C.red}/>
        </KPIRow>
        {!temDados?<div style={{marginTop:20}}><Vazio/></div>:(
          <div style={{display:'flex',gap:14,flexWrap:'wrap',marginTop:16}}>
            {pieData.length>0&&(
              <Card style={{flex:1,minWidth:200}}>
                <SecTitle>Por Status</SecTitle>
                <ResponsiveContainer width="100%" height={190}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70}
                      label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`} labelLine={false} style={{fontSize:10}}>
                      {pieData.map((_,i)=><Cell key={i} fill={[C.green,C.yellow,C.red,C.muted][i%4]}/>)}
                    </Pie>
                    <Tooltip formatter={v=>brl(v)}/>
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            )}
            {categorias.length>0&&(
              <Card style={{flex:2,minWidth:220}}>
                <SecTitle>Por Categoria</SecTitle>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead><tr><th style={th}>Categoria</th><th style={{...th,textAlign:'right'}}>Qtd.</th><th style={{...th,textAlign:'right'}}>Total</th></tr></thead>
                  <tbody>
                    {categorias.map((c,i)=>(
                      <tr key={i} style={{borderBottom:`1px solid ${C.border}`}}>
                        <td style={{...td,display:'flex',alignItems:'center',gap:8}}>
                          <span style={{width:8,height:8,borderRadius:'50%',background:CHART_COLORS[i%CHART_COLORS.length],display:'inline-block',flexShrink:0}}/>
                          {c.categoria}
                        </td>
                        <td style={{...td,textAlign:'right',color:C.muted}}>{c.quantidade}</td>
                        <td style={{...td,textAlign:'right',fontWeight:700,color:corValor}}>{brl(c.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── ABA Fechamento ─────────────────────────────────────────────────────────
function RelFechamento({mes,ano}){
  const [dados,setDados]=useState(null);
  const [loading,setLoading]=useState(true);
  const ref=useRef();
  useEffect(()=>{
    setLoading(true);
    api.get(`/relatorios/fechamento?mes=${mes}&ano=${ano}`)
      .then(r=>setDados(r.data)).catch(()=>setDados(null)).finally(()=>setLoading(false));
  },[mes,ano]);
  if(loading) return <Spinner/>;
  if(!dados) return <Vazio msg="Erro ao carregar dados."/>;
  const {dados:d,historico}=dados;
  if(!d) return <Vazio msg="Fechamento ainda não realizado para este mês."/>;

  // Valores vindos do backend (relatorios.js — calcularFechamento)
  const fat              = Number(d.faturamento          ||0);
  const desp             = Number(d.totalDespesas        ||0);
  const lucro            = Number(d.lucroMes             ||0);
  const margem           = fat>0?((lucro/fat)*100).toFixed(1):0;
  const totalContratos   = Number(d.totalContratos       ||0);
  const totalAvulsos     = Number(d.totalAvulsos         ||0);
  const totalSalarios    = Number(d.totalSalarios        ||0);
  const totalExtras      = Number(d.totalExtras          ||0);
  const totalDespFixas   = Number(d.totalDespesasFixas   ||0);
  const totalDespVar     = Number(d.totalDespesasVariaveis||0);
  const totalDespItens   = Number(d.totalDespesasItens   ||0);
  const totalReemb       = Number(d.totalReembolsosManual||0);
  const totalReservado   = Number(d.totalReservado       ||0);
  const saldoDLAnterior  = Number(d.saldoDLAnterior      ||0);
  const saldoDLAtual     = Number(d.saldoDLAtual         ||lucro);
  const poupancaPerc     = Number(d.poupancaPerc         ||20);
  const poupancaDL       = saldoDLAtual*(poupancaPerc/100);
  const divisaoSocios    = Array.isArray(d.divisaoSocios)?d.divisaoSocios:[];

  // Helper de linha do detalhamento
  const Linha=({label,valor,cor,negativo,destaque,indent})=>(
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',
      padding:destaque?'7px 8px':'5px 0',
      background:destaque?(cor===C.blue?'#f0f7ff':cor===C.red?'#fff5f5':cor===C.green?'#f0fdf4':'#f8f8f8'):'transparent',
      borderBottom:destaque?'none':`1px solid ${C.border}`,
      borderRadius:destaque?6:0,marginTop:destaque?4:0}}>
      <span style={{fontSize:12,color:C.navy,paddingLeft:indent||destaque?0:12,fontWeight:destaque?700:400}}>{label}</span>
      <span style={{fontSize:destaque?14:13,fontWeight:destaque?900:700,color:cor||C.navy}}>
        {negativo&&valor>0?'– ':''}{brl(Math.abs(valor))}
      </span>
    </div>
  );

  return(
    <div>
      <div className="no-print" style={{display:'flex',justifyContent:'flex-end',marginBottom:14}}>
        <BotaoImprimir conteudoRef={ref} titulo={`Fechamento — ${MESES[mes-1]} ${ano}`}/>
      </div>
      <div ref={ref}>
        <h2 style={{margin:'0 0 14px',fontSize:16,color:C.navy}}>📊 Fechamento — {MESES[mes-1]} / {ano}</h2>
        <KPIRow>
          <KPI label="Faturamento" valor={brl(fat)} icone="📈" cor={C.blue}/>
          <KPI label="Total Despesas" valor={brl(desp)} icone="📉" cor={C.red}/>
          <KPI label="Lucro do Mês" valor={brl(lucro)} icone="💎" cor={lucro>=0?C.green:C.red}/>
          <KPI label="Margem" valor={`${margem}%`} icone="📊" cor={C.orange}/>
        </KPIRow>

        {historico?.length>0&&(
          <Card style={{marginTop:16}}>
            <SecTitle>Tendência — Últimos 6 Meses</SecTitle>
            <ResponsiveContainer width="100%" height={230}>
              <LineChart data={historico} margin={{left:10,right:10}}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border}/>
                <XAxis dataKey="label" tick={{fontSize:11,fill:C.muted}}/>
                <YAxis tick={{fontSize:11,fill:C.muted}} tickFormatter={v=>`R$${(v/1000).toFixed(0)}k`}/>
                <Tooltip content={<CustomTooltip prefixo="brl"/>}/>
                <Legend wrapperStyle={{fontSize:12}}/>
                <Line type="monotone" dataKey="faturamento" name="Faturamento" stroke={C.blue} strokeWidth={2.5} dot={{r:4}}/>
                <Line type="monotone" dataKey="despesaTotal" name="Despesas" stroke={C.red} strokeWidth={2.5} dot={{r:4}}/>
                <Line type="monotone" dataKey="lucro" name="Lucro" stroke={C.green} strokeWidth={2.5} dot={{r:4}}/>
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}

        <Card style={{marginTop:16}}>
          <SecTitle>Detalhamento</SecTitle>

          {/* RECEITA */}
          <div style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:.5,marginBottom:6}}>Receita</div>
            <Linha label="Total Contratos"                               valor={totalContratos} cor={C.blue}/>
            <Linha label="Total Contas"                                  valor={fat}            cor={C.orange}/>
            <Linha label="Serviços Avulsos (Contas − Contratos)"         valor={totalAvulsos}   cor={C.green} destaque/>
          </div>

          {/* DESPESAS */}
          <div style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:.5,marginBottom:6}}>Despesas</div>
            <Linha label="Salários / Quotas"    valor={totalSalarios} negativo/>
            <Linha label="13° / Férias"         valor={totalExtras}   negativo/>
            {totalDespFixas>0
              ? <Linha label="Despesas Fixas"    valor={totalDespFixas}  negativo/>
              : null}
            {totalDespVar>0
              ? <Linha label="Despesas Variáveis" valor={totalDespVar}   negativo/>
              : null}
            {totalDespFixas===0&&totalDespVar===0&&totalDespItens>0
              ? <Linha label="Despesas Fixas e Variáveis" valor={totalDespItens} negativo/>
              : null}
            {totalReemb>0&&<Linha label="Reembolsos" valor={totalReemb} negativo/>}
            <Linha label="Total Despesas" valor={desp} cor={C.red} destaque/>
          </div>

          {/* RESULTADO */}
          <div style={{marginBottom:totalReservado>0||saldoDLAtual!==0?16:0}}>
            <Linha label="Lucro do Mês" valor={lucro} cor={lucro>=0?C.green:C.red} destaque/>
            {totalReservado>0&&<Linha label="Reservado" valor={totalReservado} cor={C.green}/>}
          </div>

          {/* SALDO D.L. */}
          {(saldoDLAtual!==0||saldoDLAnterior!==0)&&(
            <div style={{marginBottom:16,padding:'12px 14px',background:'#eef2ff',borderRadius:10}}>
              <div style={{fontSize:11,fontWeight:700,color:C.blue,textTransform:'uppercase',letterSpacing:.5,marginBottom:8}}>Saldo D.L.</div>
              {saldoDLAnterior!==0&&<Linha label="Saldo D.L. Anterior" valor={saldoDLAnterior} cor={C.blue} indent/>}
              <Linha label="Saldo D.L. Atual" valor={Math.abs(saldoDLAtual)} cor={saldoDLAtual>=0?C.blue:C.red} negativo={saldoDLAtual<0} destaque/>
            </div>
          )}

          {/* DIVISÃO D.L. */}
          {divisaoSocios.length>0&&(
            <div style={{padding:'12px 14px',background:'#f0fdf4',borderRadius:10}}>
              <div style={{fontSize:11,fontWeight:700,color:C.green,textTransform:'uppercase',letterSpacing:.5,marginBottom:8}}>
                Divisão D.L. (sobre Saldo D.L. Atual)
              </div>
              <Linha label={`Poupança (${poupancaPerc}%)`} valor={Math.abs(poupancaDL)} cor={poupancaDL>=0?C.green:C.red} negativo={poupancaDL<0} indent/>
              {divisaoSocios.length>0&&(
                <>
                  <div style={{fontSize:11,fontWeight:700,color:C.muted,margin:'8px 0 4px'}}>
                    RESTANTE ({100-poupancaPerc}%) DIVIDIDO:
                  </div>
                  {divisaoSocios.map((s,i)=>(
                    <Linha key={i} label={`${s.nome} (${s.percentual}%)`} valor={Math.abs(s.valor)} cor={s.valor>=0?C.navy:C.red} negativo={s.valor<0} indent/>
                  ))}
                </>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ── Tabela de tickets (usada na tela com paginação) ──────────────────────────
function TabelaTickets({ lista }) {
  return (
    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
      <thead><tr>
        <th style={{...th,width:55,minWidth:55}}>#</th>
        <th style={{...th,minWidth:180}}>Título / Cliente</th>
        <th style={th}>Operador</th>
        <th style={{...th,minWidth:120}}>Abertura</th>
        <th style={{...th,minWidth:120}}>Finalização</th>
        <th style={th}>Status</th>
        <th style={{...th,textAlign:'right',minWidth:55}}>Tempo</th>
      </tr></thead>
      <tbody>
        {lista.map((t,i)=>(
          <tr key={i} style={{borderBottom:`1px solid ${C.border}`}}>
            <td style={{...td,fontWeight:700,color:C.orange,whiteSpace:'nowrap',width:55,minWidth:55}}>
              {(t.numero||t.id) ? `#${t.numero||t.id}` : '—'}
            </td>
            <td style={{...td,minWidth:180}}>
              <div style={{fontWeight:600,maxWidth:240,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={t.titulo}>{t.titulo||'—'}</div>
              <div style={{fontSize:10,color:C.muted}}>{t.cliente_nome}</div>
            </td>
            <td style={{...td,whiteSpace:'nowrap'}}>{t.operador||'—'}</td>
            <td style={{...td,whiteSpace:'nowrap',color:C.muted,fontSize:11,minWidth:120}}>{t.criado_em_fmt}</td>
            <td style={{...td,whiteSpace:'nowrap',color:C.muted,fontSize:11,minWidth:120}}>{t.fechado_em_fmt}</td>
            <td style={td}><StatusBadge status={t.status}/></td>
            <td style={{...td,textAlign:'right',whiteSpace:'nowrap',fontWeight:600,minWidth:55}}>{t.tempo_formatado}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


// ── ABA Tickets ────────────────────────────────────────────────────────────
const POR_PAGINA = 10;

function RelTickets({mes,ano,toast}){
  const [dados,setDados]=useState(null);
  const [loading,setLoading]=useState(true);
  const [enviando,setEnviando]=useState(false);
  const [filtroCliente,setFiltroCliente]=useState('');
  const [buscaAtiva,setBuscaAtiva]=useState('');
  const [pagina,setPagina]=useState(1);
  // ref da área visível na tela (com paginação) — usada para imprimir
  const refImpressao=useRef();
  const timerRef=useRef();

  const carregar=useCallback((busca='')=>{
    setLoading(true);
    setPagina(1);
    const q=busca?`&cliente=${encodeURIComponent(busca)}`:'';
    api.get(`/relatorios/tickets?mes=${mes}&ano=${ano}${q}`)
      .then(r=>setDados(r.data)).catch(()=>setDados(null)).finally(()=>setLoading(false));
  },[mes,ano]);

  useEffect(()=>{ carregar(''); setFiltroCliente(''); setBuscaAtiva(''); },[carregar]);

  const handleFiltro=(v)=>{
    setFiltroCliente(v);
    clearTimeout(timerRef.current);
    timerRef.current=setTimeout(()=>{ setBuscaAtiva(v); carregar(v); },500);
  };

  const handleEnviarEmail=async()=>{
    setEnviando(true);
    try{
      const body={mes,ano};
      if(buscaAtiva) body.cliente=buscaAtiva;
      const r=await api.post('/relatorios/tickets/enviar-email',body);
      if(r.data.enviados>0) toast(`✅ Relatório enviado para ${r.data.enviados} cliente(s)!`,'success');
      else toast('Nenhum cliente com email encontrado.','');
      if(r.data.erros?.length>0) toast(`⚠️ Falha: ${r.data.erros.join(', ')}`,'error');
    }catch(e){
      toast(e?.response?.data?.erro||'Erro ao enviar emails.','error');
    }finally{ setEnviando(false); }
  };

  // Impressão: estilo igual à tela — sem "Clientes Atendidos", sem paginação, todos os tickets
  const handleImprimir=()=>{
    const todos     = dados?.listaTickets || [];
    const clientes  = dados?.porCliente   || [];
    const totalTkts = totalTicketsTela;
    const totalSegImp = Number(dados?.resumo?.total_segundos_pdf || 0);
    const hImp = Math.floor(totalSegImp/3600);
    const mImp = Math.floor((totalSegImp%3600)/60);
    const horasImp = totalSegImp>0 ? (hImp>0?`${hImp}h ${mImp}m`:`${mImp}m`) : '—';

    // Usa nome completo do banco, não o termo buscado
    const nomeImp = dados?.nomeClienteFiltrado || buscaAtiva || '';
    const nomeImpUpper = nomeImp.toUpperCase();
    const tituloImp = nomeImp
      ? `Tickets — ${MESES[mes-1]} / ${ano} · ${nomeImpUpper}`
      : `Tickets — ${MESES[mes-1]} / ${ano}`;

    // Linhas da tabela por cliente
    const linhasClientes = clientes.map(c=>`
      <tr style="border-bottom:1px solid #d4dde8">
        <td style="padding:9px 12px">
          <div style="font-weight:600">${c.cliente}</div>
          ${c.solicitante_email?`<div style="font-size:10px;color:#6b7a8d">${c.solicitante_email}</div>`:''}
        </td>
        <td style="padding:9px 12px;text-align:center;font-weight:700">${c.total}</td>
        <td style="padding:9px 12px;text-align:center;font-weight:600;color:#27ae60">${c.fechados}</td>
        <td style="padding:9px 12px;text-align:center;font-weight:600;color:${(c.abertos+c.em_atendimento)>0?'#D85A30':'#6b7a8d'}">${c.abertos+c.em_atendimento}</td>
      </tr>`).join('');

    const linhasTickets = todos.map(t=>`
      <tr style="border-bottom:1px solid #f0f3f7">
        <td style="padding:7px 8px;font-weight:700;color:#D85A30;white-space:nowrap">
          ${(t.numero||t.id)?`#${t.numero||t.id}`:'—'}
        </td>
        <td style="padding:7px 8px">
          <div style="font-weight:600">${t.titulo||'—'}</div>
          <div style="font-size:10px;color:#6b7a8d">${t.cliente_nome}</div>
        </td>
        <td style="padding:7px 8px;white-space:nowrap">${t.operador||'—'}</td>
        <td style="padding:7px 8px;white-space:nowrap;font-size:11px;color:#6b7a8d">${t.criado_em_fmt}</td>
        <td style="padding:7px 8px;white-space:nowrap;font-size:11px;color:#6b7a8d">${t.fechado_em_fmt}</td>
        <td style="padding:7px 8px">
          <span style="padding:2px 7px;border-radius:20px;font-size:10px;font-weight:700;background:${t.status==='fechado'?'#f0fdf4':t.status==='em_atendimento'?'#eff6ff':'#fff7ed'};color:${t.status==='fechado'?'#15803d':t.status==='em_atendimento'?'#1e40af':'#c2410c'}">
            ${t.status==='fechado'?'Concluído':t.status==='em_atendimento'?'Em Atendimento':'Aberto'}
          </span>
        </td>
        <td style="padding:7px 8px;text-align:right;font-weight:600">${t.tempo_formatado}</td>
      </tr>`).join('');

    const win = window.open('','_blank');
    win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${tituloImp}</title>
<style>
*{box-sizing:border-box}
body{font-family:Arial,sans-serif;font-size:12px;color:#1A2233;margin:0;padding:20px;background:white}
h2{color:#1A2233;margin:0 0 6px;font-size:16px;font-weight:900}
.kpi-row{display:flex;gap:12px;margin-bottom:18px}
.kpi{border:1px solid #d4dde8;border-radius:10px;padding:14px 18px;flex:1}
.kpi-label{font-size:9px;font-weight:700;color:#6b7a8d;text-transform:uppercase;letter-spacing:.6px;margin-bottom:5px}
.kpi-total{font-size:22px;font-weight:900;color:#D85A30}
.kpi-horas{font-size:22px;font-weight:900;color:#2563eb}
.sec{font-size:10px;font-weight:700;color:#1A2233;text-transform:uppercase;letter-spacing:.8px;border-left:3px solid #D85A30;padding-left:8px;margin:18px 0 10px}
.card{border:1px solid #d4dde8;border-radius:10px;padding:14px;margin-bottom:14px}
table{width:100%;border-collapse:collapse;font-size:11px}
th{background:#f5f7fa;padding:8px 10px;font-size:9px;font-weight:700;text-transform:uppercase;color:#6b7a8d;text-align:left;white-space:nowrap}
@media print{body{padding:10px}}
</style></head><body>

<h2>🎫 ${tituloImp}</h2>

<div class="kpi-row">
  <div class="kpi">
    <div class="kpi-label">🎫 Total de Tickets</div>
    <div class="kpi-total">${totalTkts}</div>
  </div>
  <div class="kpi">
    <div class="kpi-label">⏱️ Total de Horas</div>
    <div class="kpi-horas">${horasImp}</div>
  </div>
</div>

${clientes.length>0?`
<div class="card">
  <div class="sec">Tickets por Cliente</div>
  <table>
    <thead><tr>
      <th>Cliente</th>
      <th style="text-align:center">Total</th>
      <th style="text-align:center">Concluídos</th>
      <th style="text-align:center">Em Aberto</th>
    </tr></thead>
    <tbody>${linhasClientes}</tbody>
  </table>
</div>`:''}

${todos.length>0?`
<div class="card">
  <div class="sec">Lista Detalhada</div>
  <table>
    <thead><tr>
      <th style="width:55px">#</th>
      <th>Título / Cliente</th>
      <th>Operador</th>
      <th>Abertura</th>
      <th>Finalização</th>
      <th>Status</th>
      <th style="text-align:right">Tempo</th>
    </tr></thead>
    <tbody>${linhasTickets}</tbody>
  </table>
</div>`:''}

</body></html>`);
    win.document.close();
    setTimeout(()=>{ win.focus(); win.print(); win.close(); },400);
  };

  const pieStatus=(dados?.porStatus||[]).map(s=>({
    name:statusLabel[s.status]||s.status,value:Number(s.total),cor:statusCor[s.status]||C.muted
  })).filter(s=>s.value>0);
  const resumo=dados?.resumo;
  const temDados=Number(resumo?.total||0)>0;
  const lista = dados?.listaTickets || [];
  const totalPaginas = Math.ceil(lista.length / POR_PAGINA);
  const listaVis = lista.slice((pagina-1)*POR_PAGINA, pagina*POR_PAGINA);

  // Nome completo do cliente (do banco) ou o termo buscado como fallback
  const nomeClienteCompleto = dados?.nomeClienteFiltrado || buscaAtiva;
  const clienteUpper = nomeClienteCompleto ? nomeClienteCompleto.toUpperCase() : '';

  const tituloRel = buscaAtiva
    ? `🎫 Tickets — ${MESES[mes-1]} / ${ano} · ${clienteUpper}`
    : `🎫 Tickets — ${MESES[mes-1]} / ${ano}`;

  const totalSeg = Number(resumo?.total_segundos || 0);

  // Se há filtro ativo, usa totais do cliente filtrado (soma de porCliente)
  const totalTicketsTela = buscaAtiva
    ? (dados?.porCliente || []).reduce((a, c) => a + Number(c.total), 0)
    : Number(resumo?.total || 0);

  const totalHorasTela = buscaAtiva
    ? Number(resumo?.total_segundos_pdf || 0)
    : totalSeg;

  const totalHTelaH = Math.floor(totalHorasTela / 3600);
  const totalHTelaM = Math.floor((totalHorasTela % 3600) / 60);
  const totalHorasTelaLabel = totalHorasTela > 0
    ? totalHTelaH > 0 ? `${totalHTelaH}h ${totalHTelaM}m` : `${totalHTelaM}m`
    : '—';

  return(
    <div>
      {/* Toolbar */}
      <div className="no-print" style={{display:'flex',gap:10,alignItems:'center',justifyContent:'space-between',marginBottom:14,flexWrap:'wrap'}}>
        <div style={{position:'relative',flex:1,minWidth:180,maxWidth:300}}>
          <span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:C.muted,fontSize:14}}>🔍</span>
          <input type="text" value={filtroCliente} onChange={e=>handleFiltro(e.target.value)}
            placeholder="Filtrar por cliente..."
            style={{width:'100%',padding:'8px 12px 8px 32px',border:`1.5px solid ${C.border}`,borderRadius:8,fontSize:13,color:C.navy,outline:'none',boxSizing:'border-box'}}
            onFocus={e=>e.target.style.borderColor=C.orange}
            onBlur={e=>e.target.style.borderColor=C.border}
          />
        </div>
        <div style={{display:'flex',gap:8}}>
          {/* Imprimir chama handleImprimir (todos, sem paginação) */}
          <button onClick={handleImprimir} style={{
            display:'flex',alignItems:'center',gap:6,padding:'8px 16px',
            border:`1.5px solid ${C.border}`,borderRadius:8,background:'white',
            color:C.navy,fontSize:13,fontWeight:600,cursor:'pointer',transition:'all .15s',
          }}
            onMouseEnter={e=>{e.currentTarget.style.borderColor=C.orange;e.currentTarget.style.color=C.orange;}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.color=C.navy;}}
          >🖨️ Imprimir</button>
          <button onClick={handleEnviarEmail} disabled={enviando||!temDados} style={{
            padding:'8px 16px',border:'none',
            background:enviando||!temDados?C.border:C.orange,
            color:enviando||!temDados?C.muted:'white',
            borderRadius:8,fontSize:13,fontWeight:700,
            cursor:enviando||!temDados?'not-allowed':'pointer',
            display:'flex',alignItems:'center',gap:6,transition:'background .2s',
          }}
            onMouseEnter={e=>{if(!enviando&&temDados)e.currentTarget.style.background=C.orangeH;}}
            onMouseLeave={e=>{if(!enviando&&temDados)e.currentTarget.style.background=C.orange;}}
          >{enviando?'⏳ Enviando...':'📧 Enviar por Email'}</button>
        </div>
      </div>

      {loading?<Spinner/>:(
        <div>
          <h2 style={{margin:'0 0 14px',fontSize:16,color:C.navy}}>{tituloRel}</h2>

          {/* KPIs: Total + Total de Horas + Clientes Atendidos */}
          <KPIRow>
            <KPI label="Total de Tickets" valor={totalTicketsTela} icone="🎫" cor={C.orange}/>
            <KPI label="Total de Horas" valor={totalHorasTelaLabel} icone="⏱️" cor={C.blue}/>
            <KPI label="Clientes Atendidos" valor={dados?.porCliente?.length||0} icone="👥" cor={C.navy}/>
          </KPIRow>

          {!temDados?<div style={{marginTop:20}}><Vazio/></div>:(
            <>
              {/* Gráficos só sem filtro */}
              {!buscaAtiva&&(
                <div style={{display:'flex',gap:14,flexWrap:'wrap',marginTop:16}}>
                  {pieStatus.length>0&&(
                    <Card style={{flex:1,minWidth:200}}>
                      <SecTitle>Por Status</SecTitle>
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie data={pieStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65}
                            label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`} labelLine={false} style={{fontSize:10}}>
                            {pieStatus.map((s,i)=><Cell key={i} fill={s.cor}/>)}
                          </Pie>
                          <Tooltip/>
                        </PieChart>
                      </ResponsiveContainer>
                    </Card>
                  )}
                  {dados?.porCategoria?.length>0&&(
                    <Card style={{flex:1,minWidth:200}}>
                      <SecTitle>Por Categoria</SecTitle>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={dados.porCategoria.slice(0,6)} layout="vertical" margin={{left:60,right:20}}>
                          <XAxis type="number" tick={{fontSize:10,fill:C.muted}}/>
                          <YAxis type="category" dataKey="categoria" tick={{fontSize:10,fill:C.muted}} width={55}/>
                          <Tooltip/>
                          <Bar dataKey="total" name="Tickets" radius={[0,4,4,0]}>
                            {dados.porCategoria.slice(0,6).map((_,i)=><Cell key={i} fill={CHART_COLORS[i%CHART_COLORS.length]}/>)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                  )}
                </div>
              )}

              {/* Resumo por cliente */}
              {dados?.porCliente?.length>0&&(
                <Card style={{marginTop:16}}>
                  <SecTitle>Tickets por Cliente</SecTitle>
                  <div style={{overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                      <thead><tr>
                        <th style={th}>Cliente</th>
                        <th style={{...th,textAlign:'center'}}>Total</th>
                        <th style={{...th,textAlign:'center'}}>Concluídos</th>
                        <th style={{...th,textAlign:'center'}}>Em Aberto</th>
                      </tr></thead>
                      <tbody>
                        {dados.porCliente.map((c,i)=>(
                          <tr key={i} style={{borderBottom:`1px solid ${C.border}`}}>
                            <td style={td}>
                              <div style={{fontWeight:600}}>{c.cliente}</div>
                              {c.solicitante_email&&<div style={{fontSize:10,color:C.muted}}>{c.solicitante_email}</div>}
                            </td>
                            <td style={{...td,textAlign:'center',fontWeight:700}}>{c.total}</td>
                            <td style={{...td,textAlign:'center',color:C.green,fontWeight:600}}>{c.fechados}</td>
                            <td style={{...td,textAlign:'center',color:(c.abertos+c.em_atendimento)>0?C.orange:C.muted,fontWeight:600}}>{c.abertos+c.em_atendimento}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              {/* Tabela detalhada com paginação */}
              {lista.length>0&&(
                <Card style={{marginTop:16}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:8}}>
                    <SecTitle style={{margin:0}}>Lista Detalhada</SecTitle>
                    <span style={{fontSize:12,color:C.muted}}>{lista.length} ticket(s) no total</span>
                  </div>
                  <div style={{overflowX:'auto'}}>
                    <TabelaTickets lista={listaVis}/>
                  </div>

                  {/* Paginação */}
                  {totalPaginas>1&&(
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:14,paddingTop:12,borderTop:`1px solid ${C.border}`,flexWrap:'wrap',gap:8}}>
                      <span style={{fontSize:12,color:C.muted}}>
                        Página {pagina} de {totalPaginas} · {lista.length} registros
                      </span>
                      <div style={{display:'flex',gap:6}}>
                        <button onClick={()=>setPagina(p=>Math.max(1,p-1))} disabled={pagina===1}
                          style={{padding:'5px 12px',border:`1.5px solid ${C.border}`,borderRadius:6,background:'white',color:pagina===1?C.muted:C.navy,fontSize:12,fontWeight:600,cursor:pagina===1?'not-allowed':'pointer'}}>
                          ← Anterior
                        </button>
                        {/* Botões de página */}
                        {Array.from({length:totalPaginas},(_,i)=>i+1)
                          .filter(p=>p===1||p===totalPaginas||Math.abs(p-pagina)<=1)
                          .reduce((acc,p,idx,arr)=>{
                            if(idx>0&&p-arr[idx-1]>1) acc.push('...');
                            acc.push(p);
                            return acc;
                          },[])
                          .map((p,i)=>
                            p==='...'
                              ? <span key={`d${i}`} style={{padding:'5px 4px',fontSize:12,color:C.muted}}>…</span>
                              : <button key={p} onClick={()=>setPagina(p)}
                                  style={{padding:'5px 10px',border:`1.5px solid ${p===pagina?C.orange:C.border}`,borderRadius:6,background:p===pagina?C.orange:'white',color:p===pagina?'white':C.navy,fontSize:12,fontWeight:600,cursor:'pointer'}}>
                                  {p}
                                </button>
                          )
                        }
                        <button onClick={()=>setPagina(p=>Math.min(totalPaginas,p+1))} disabled={pagina===totalPaginas}
                          style={{padding:'5px 12px',border:`1.5px solid ${C.border}`,borderRadius:6,background:'white',color:pagina===totalPaginas?C.muted:C.navy,fontSize:12,fontWeight:600,cursor:pagina===totalPaginas?'not-allowed':'pointer'}}>
                          Próximo →
                        </button>
                      </div>
                    </div>
                  )}
                </Card>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Componente principal ───────────────────────────────────────────────────
export default function Relatorios({usuario,toast,modulos=[]}){
  const {mes:mesI,ano:anoI}=mesAtual();
  const [mes,setMes]=useState(mesI);
  const [ano,setAno]=useState(anoI);
  const [aba,setAba]=useState('reembolso');
  const temTkt=Array.isArray(modulos)&&modulos.includes('tkt');
  const abas=[
    {key:'reembolso', label:'💰 Reembolsos'},
    {key:'pagar',     label:'📤 Contas a Pagar'},
    {key:'receber',   label:'📥 Contas a Receber'},
    {key:'fechamento',label:'📊 Fechamento'},
    ...(temTkt?[{key:'tickets',label:'🎫 Tickets'}]:[]),
  ];
  return(
    <div style={{paddingBottom:40}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:12,marginBottom:20}}>
        <div>
          <h2 style={{margin:0,fontSize:22,fontWeight:900,color:C.navy}}>📊 Relatórios</h2>
          <p style={{margin:'4px 0 0',fontSize:13,color:C.muted}}>{MESES[mes-1]} / {ano}</p>
        </div>
        <FiltroMes mes={mes} ano={ano} onChange={(m,a)=>{setMes(m);setAno(a);}}/>
      </div>
      <div style={{display:'flex',borderBottom:`1px solid ${C.border}`,marginBottom:22,overflowX:'auto',scrollbarWidth:'none'}}>
        {abas.map(a=>(
          <button key={a.key} onClick={()=>setAba(a.key)} style={{
            padding:'10px 15px',border:'none',background:'none',
            fontSize:13,fontWeight:aba===a.key?700:500,
            color:aba===a.key?C.orange:C.muted,
            borderBottom:aba===a.key?`2.5px solid ${C.orange}`:'2.5px solid transparent',
            cursor:'pointer',whiteSpace:'nowrap',flexShrink:0,transition:'all .15s',
          }}>{a.label}</button>
        ))}
      </div>
      {aba==='reembolso'  && <RelReembolso  mes={mes} ano={ano}/>}
      {aba==='pagar'      && <RelFinanceiro  mes={mes} ano={ano} tipo="pagar"/>}
      {aba==='receber'    && <RelFinanceiro  mes={mes} ano={ano} tipo="receber"/>}
      {aba==='fechamento' && <RelFechamento  mes={mes} ano={ano}/>}
      {aba==='tickets'&&temTkt && <RelTickets mes={mes} ano={ano} toast={toast}/>}
    </div>
  );
}
